import { useCallback, useEffect, useRef, useState } from 'react';
import { KeystrokeCapture } from '@/lib/keystroke-capture';
import type {
  KeystrokeEvent,
  BiometricFeatures,
  CaptureState,
  CaptureAnomaly,
  CaptureConfig,
} from '@/types/keystroke';
import type { WorkerRequest, WorkerResponse } from '@/workers/feature-worker';

interface UseKeystrokeCaptureOptions {
  config?: Partial<CaptureConfig>;
  /** Auto-start capture on mount */
  autoStart?: boolean;
  /** Extract features after this many events */
  extractAfter?: number;
  /** Debounce extraction in ms */
  extractDebounce?: number;
}

interface UseKeystrokeCaptureReturn {
  /** Ref callback to attach to the capturing element */
  captureRef: (el: HTMLElement | null) => void;
  /** Current capture state */
  state: CaptureState;
  /** All captured events */
  events: KeystrokeEvent[];
  /** Extracted features (null until extraction) */
  features: BiometricFeatures | null;
  /** Whether features are being extracted */
  extracting: boolean;
  /** Recent flight times for waveform (last 30) */
  waveformData: number[];
  /** Detected anomalies */
  anomalies: CaptureAnomaly[];
  /** Total keystroke count */
  keystrokeCount: number;
  /** Start capturing */
  start: () => void;
  /** Pause capturing */
  pause: () => void;
  /** Resume capturing */
  resume: () => void;
  /** Stop capturing */
  stop: () => void;
  /** Reset all data */
  reset: () => void;
  /** Manually trigger feature extraction */
  extractFeatures: () => Promise<BiometricFeatures | null>;
  /** Access the raw capture instance */
  capture: KeystrokeCapture;
}

export function useKeystrokeCapture(
  options: UseKeystrokeCaptureOptions = {},
): UseKeystrokeCaptureReturn {
  const {
    config,
    autoStart = false,
    extractAfter = 50,
    extractDebounce = 1000,
  } = options;

  const captureRef = useRef<KeystrokeCapture>(new KeystrokeCapture(config));
  const workerRef = useRef<Worker | null>(null);
  const extractTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingExtractionsRef = useRef<Map<string, {
    resolve: (f: BiometricFeatures | null) => void;
    reject: (e: Error) => void;
  }>>(new Map());

  const [state, setState] = useState<CaptureState>('idle');
  const [events, setEvents] = useState<KeystrokeEvent[]>([]);
  const [features, setFeatures] = useState<BiometricFeatures | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [waveformData, setWaveformData] = useState<number[]>([]);
  const [anomalies, setAnomalies] = useState<CaptureAnomaly[]>([]);
  const [keystrokeCount, setKeystrokeCount] = useState(0);

  // Initialize Web Worker
  useEffect(() => {
    workerRef.current = new Worker(
      new URL('../workers/feature-worker.ts', import.meta.url),
      { type: 'module' },
    );

    workerRef.current.onmessage = (e: MessageEvent<WorkerResponse>) => {
      const { type, id, features: resultFeatures, error } = e.data;
      const pending = pendingExtractionsRef.current.get(id);
      if (!pending) return;

      pendingExtractionsRef.current.delete(id);
      setExtracting(pendingExtractionsRef.current.size > 0);

      if (type === 'result' && resultFeatures) {
        setFeatures(resultFeatures);
        pending.resolve(resultFeatures);
      } else {
        console.error('[useKeystrokeCapture] Worker error:', error);
        pending.resolve(null);
      }
    };

    return () => {
      workerRef.current?.terminate();
      workerRef.current = null;
    };
  }, []);

  // Setup event handlers
  useEffect(() => {
    const capture = captureRef.current;

    capture.onEvents((evts) => {
      const keydownCount = evts.filter((e) => e.type === 'keydown').length;
      setKeystrokeCount(keydownCount);
      setEvents([...evts]);
      setWaveformData(capture.getRecentFlightTimes(30));

      // Auto-extract after threshold
      if (keydownCount >= extractAfter && keydownCount % 10 === 0) {
        if (extractTimerRef.current) clearTimeout(extractTimerRef.current);
        extractTimerRef.current = setTimeout(() => {
          doExtract(evts);
        }, extractDebounce);
      }
    });

    capture.onAnomaly((anomaly) => {
      setAnomalies((prev) => [...prev, anomaly]);
    });

    return () => {
      if (extractTimerRef.current) clearTimeout(extractTimerRef.current);
    };
  }, [extractAfter, extractDebounce]);

  // Send events to worker for extraction
  const doExtract = useCallback((evts: KeystrokeEvent[]): Promise<BiometricFeatures | null> => {
    if (!workerRef.current || evts.length < 4) {
      return Promise.resolve(null);
    }

    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setExtracting(true);

    return new Promise((resolve, reject) => {
      pendingExtractionsRef.current.set(id, { resolve, reject });
      const msg: WorkerRequest = { type: 'extract', id, events: evts };
      workerRef.current!.postMessage(msg);
    });
  }, []);

  // Ref callback to attach to DOM element
  const setRef = useCallback((el: HTMLElement | null) => {
    const capture = captureRef.current;
    if (el) {
      capture.attach(el);
      if (autoStart) {
        capture.start();
        setState('capturing');
      }
    } else {
      capture.detach();
    }
  }, [autoStart]);

  const start = useCallback(() => {
    captureRef.current.start();
    setState('capturing');
  }, []);

  const pause = useCallback(() => {
    captureRef.current.pause();
    setState('paused');
  }, []);

  const resume = useCallback(() => {
    captureRef.current.resume();
    setState('capturing');
  }, []);

  const stop = useCallback(() => {
    captureRef.current.stop();
    setState('completed');
  }, []);

  const reset = useCallback(() => {
    captureRef.current.reset();
    setState('idle');
    setEvents([]);
    setFeatures(null);
    setWaveformData([]);
    setAnomalies([]);
    setKeystrokeCount(0);
  }, []);

  const extractFeatures = useCallback(async (): Promise<BiometricFeatures | null> => {
    const evts = captureRef.current.getEvents();
    return doExtract(evts);
  }, [doExtract]);

  return {
    captureRef: setRef,
    state,
    events,
    features,
    extracting,
    waveformData,
    anomalies,
    keystrokeCount,
    start,
    pause,
    resume,
    stop,
    reset,
    extractFeatures,
    capture: captureRef.current,
  };
}
